import React, { useState, useEffect, useMemo, type Key } from 'react';
import { Table, Tag, Button, Space, Pagination, message, Tooltip, Input, App, Typography, Card } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, PrinterOutlined, CheckCircleOutlined, SearchOutlined, DatabaseOutlined, CheckCircleOutlined as CheckCircleIcon, WarningOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { StandardCard, ExportExcelButton, ExportPDFButton } from '../../common';
import { theme } from '../../../theme';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text, Title } = Typography;

interface OrdenCompra {
  id: number;
  nro_comprobante: string;
  padron: string;
  fecha_emision: string;
  subtotal: number;
  iva: number;
  iinterno: number;
  total: number;
  alta: number;
  autorizada: boolean;
}

const ListarOrdenCompra: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [ordenes, setOrdenes] = useState<OrdenCompra[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, isLoading } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);

  // Obtener lista única de proveedores para el filtro
  const proveedoresUnicos = useMemo(() => {
    const proveedores = Array.from(new Set(ordenes.map(orden => orden.padron)));
    return proveedores.map(proveedor => ({
      text: proveedor,
      value: proveedor
    }));
  }, [ordenes]);

  const handleEditar = (ordenId: number) => {
    console.log('Editar:', ordenId);
    navigate(`/editar_ocompra/${ordenId}`);
  };

  const handleImprimir = (ordenId: number) => {
    navigate(`/imprimir_ocompra/${ordenId}`);
  };

  const handleAutorizar = async (record: OrdenCompra) => {
    try {
      const token = await getAccessTokenSilently();

      // Verificar si ya está autorizada
      if (record.alta === 2) {
      messageApi.error(
        `La Orden de Compra ${record.nro_comprobante} ya está Autorizada`,
        5
      );
        return;
      }

      const autorizacionData = {
        fecha: new Date().toISOString().split('T')[0],
        comprobante: record.id,
        identificacion: user?.nickname || '',
        mail: user?.email || '',
        id_empresa: empresa,
        id_sucursal: sucursal
      };

      // Actualizar estado del comprobante
     // const updateResponse = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/crear_autorizacion/${selectedRowId}`, {
     //   method: 'POST',
     //   headers: {
     //     'Authorization': `Bearer ${token}`,
     //     'Content-Type': 'application/json',
     //   },
     //   body: JSON.stringify(autorizacionData)
     // });

     // if (!updateResponse.ok) {
     //   throw new Error('Error al actualizar el estado de la orden');
     // }

      // Crear registro de autorización
      const autorizacionResponse = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/crear_autorizacion/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(autorizacionData)
      });

      if (!autorizacionResponse.ok) {
        throw new Error('Error al crear la autorización');
      }

      messageApi.success(
        `Orden de Compra ${record.nro_comprobante} autorizada por ${user?.nickname}`,
        5
      );

      // Recargar la lista de órdenes
      fetchOrdenes();

    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al autorizar la orden de compra');
    }
  };

  const handleOrdenCompraNueva = () => {
    navigate('/crear_ordencompra');
  };

  const columns: ColumnsType<OrdenCompra> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Nº Comprobante</span>,
      dataIndex: 'nro_comprobante',
      key: 'nro_comprobante',
      width: 120,
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {globalSearch ? highlightText(text, globalSearch) : text}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Proveedor</span>,
      dataIndex: 'padron',
      key: 'padron',
      width: 200,
      filters: proveedoresUnicos,
      onFilter: (value: boolean | Key, record: OrdenCompra) => 
        record.padron === String(value),
      filterSearch: true,
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {globalSearch ? highlightText(text || '', globalSearch) : text || '-'}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Emisión</span>,
      dataIndex: 'fecha_emision',
      key: 'fecha_emision',
      width: 120,
      render: (fecha: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {new Date(fecha).toLocaleDateString('es-AR')}
        </span>
      ),
      sorter: (a, b) => 
        new Date(a.fecha_emision).getTime() - new Date(b.fecha_emision).getTime(),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Subtotal</span>,
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 120,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {value.toLocaleString('es-AR', {
            style: 'currency',
            currency: 'ARS'
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>IVA</span>,
      dataIndex: 'iva',
      key: 'iva',
      width: 100,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {value.toLocaleString('es-AR', {
            style: 'currency',
            currency: 'ARS'
          })}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Total</span>,
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (value: number) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>
          {value.toLocaleString('es-AR', {
            style: 'currency',
            currency: 'ARS'
          })}
        </span>
      ),
      sorter: (a, b) => (a.total || 0) - (b.total || 0),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      width: 120,
      align: 'center' as const,
      filters: [
        { text: 'Autorizada', value: 2 },
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value: boolean | Key, record: OrdenCompra) => {
        const numValue = typeof value === 'number' ? value : Number(value);
        return record.alta === numValue;
      },
      render: (alta: number) => {
        switch(alta) {
          case 2:
            return <Tag color="blue" style={{ fontWeight: 500 }}>Autorizada</Tag>;
          case 1:
            return <Tag color="green" style={{ fontWeight: 500 }}>Activo</Tag>;
          case 0:
            return <Tag color="red" style={{ fontWeight: 500 }}>Inactivo</Tag>;
          default:
            return <Tag color="default" style={{ fontWeight: 500 }}>Desconocido</Tag>;
        }
      },
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      width: 140,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditar(record.id)}
              style={{
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary
              }}
            />
          </Tooltip>
          <Tooltip title="Imprimir">
            <Button
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handleImprimir(record.id)}
              style={{
                color: theme.colors.textSecondary,
                borderColor: theme.colors.borderLight
              }}
            />
          </Tooltip>
          {record.alta !== 2 && (
            <Tooltip title="Autorizar">
              <Button
                icon={<CheckCircleOutlined />}
                size="small"
                onClick={() => handleAutorizar(record)}
                style={{
                  backgroundColor: theme.colors.success,
                  borderColor: theme.colors.success,
                  color: 'white'
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (isLoading) return;
    console.log('empresa',empresa);
    console.log('user',user);
    fetchOrdenes();
  }, [isLoading,empresa,getAccessTokenSilently]);

  const fetchOrdenes = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_ocompra/${empresa}/${sucursal}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Error al cargar las órdenes');
      
      const data = await response.json();
      console.log('Órdenes cargadas:', data);
      setOrdenes(data);
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar las órdenes de compra');
    } finally {
      setLoading(false);
    }
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: '#fff3cd', 
          padding: '2px 4px',
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) {
      return ordenes;
    }

    const searchLower = globalSearch.toLowerCase();
    return ordenes.filter(record => {
      return (
        record.nro_comprobante.toLowerCase().includes(searchLower) ||
        (record.padron || '').toLowerCase().includes(searchLower) ||
        record.fecha_emision.toLowerCase().includes(searchLower) ||
        record.subtotal.toString().includes(searchLower) ||
        record.iva.toString().includes(searchLower) ||
        record.iinterno.toString().includes(searchLower) ||
        record.total.toString().includes(searchLower) ||
        (record.alta === 2 ? 'autorizada' : record.alta === 1 ? 'activo' : 'inactivo').includes(searchLower)
      );
    });
  }, [ordenes, globalSearch]);

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    const total = filteredData.length;
    const totalMonto = filteredData.reduce((acc, o) => acc + (o.total || 0), 0);
    const autorizadas = filteredData.filter(o => o.alta === 2).length;
    const activas = filteredData.filter(o => o.alta === 1).length;
    const promedioMonto = total > 0 ? totalMonto / total : 0;

    // Top 3 proveedores
    const proveedoresCount = filteredData.reduce((acc, record) => {
      const prov = record.padron || 'Sin proveedor';
      acc[prov] = (acc[prov] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const topProveedores = Object.entries(proveedoresCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    return {
      total,
      totalMonto,
      autorizadas,
      activas,
      promedioMonto,
      topProveedores
    };
  }, [filteredData]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => ({
          'Nº Comprobante': record.nro_comprobante,
          'Proveedor': record.padron || '',
          'Fecha Emisión': record.fecha_emision,
          'Subtotal': record.subtotal || 0,
          'IVA': record.iva || 0,
          'IIBB': record.iinterno || 0,
          'Total': record.total || 0,
          'Estado': record.alta === 2 ? 'Autorizada' : record.alta === 1 ? 'Activo' : 'Inactivo'
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Órdenes de Compra');
      XLSX.writeFile(workbook, `ordenes_compra_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Órdenes de Compra', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const tableData = filteredData.map(record => [
        record.nro_comprobante,
        (record.padron || '').substring(0, 20),
        new Date(record.fecha_emision).toLocaleDateString('es-AR'),
        (record.total || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' }),
        record.alta === 2 ? 'Autorizada' : record.alta === 1 ? 'Activo' : 'Inactivo'
      ]);

      (doc as any).autoTable({
        head: [['Nº Comp.', 'Proveedor', 'Fecha', 'Total', 'Estado']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`ordenes_compra_${new Date().toISOString().split('T')[0]}.pdf`);
      messageApi.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      messageApi.error('Error al exportar el archivo PDF');
    }
  };

//  fetchOrdenes();

  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Título y estadísticas compactas */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '28px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Órdenes de Compra
          </h1>

          {/* Estadísticas compactas en la barra superior */}
          {ordenes.length > 0 && (
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: theme.colors.backgroundGray,
                borderRadius: '6px',
                border: `1px solid ${theme.colors.borderLight}`
              }}>
                <DatabaseOutlined style={{ fontSize: '16px', color: theme.colors.primary }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Total:</Text>
                <Text strong style={{ fontSize: '14px', color: theme.colors.textPrimary }}>
                  {estadisticas.total}
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#e6f7ff',
                borderRadius: '6px',
                border: `1px solid #91d5ff`
              }}>
                <CheckCircleIcon style={{ fontSize: '16px', color: '#1890ff' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Autorizadas:</Text>
                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                  {estadisticas.autorizadas}
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#f6ffed',
                borderRadius: '6px',
                border: `1px solid #b7eb8f`
              }}>
                <CheckCircleIcon style={{ fontSize: '16px', color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>Monto Total:</Text>
                <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                  ${estadisticas.totalMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                </Text>
              </div>

              <Button
                type="text"
                size="small"
                icon={showStatsExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowStatsExpanded(!showStatsExpanded)}
                style={{
                  fontSize: '12px',
                  color: theme.colors.textSecondary
                }}
              >
                {showStatsExpanded ? 'Ocultar detalles' : 'Ver más'}
              </Button>
            </div>
          )}
        </div>

        {/* Cards de resumen expandidas (colapsables) */}
        {showStatsExpanded && ordenes.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gap: '16px', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            marginBottom: '24px'
          }}>
            <Card style={{ borderTop: `4px solid ${theme.colors.primary}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Total Órdenes</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0' }}>{estadisticas.total}</Title>
                </div>
                <DatabaseOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #1890ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Autorizadas</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#1890ff' }}>
                    {estadisticas.autorizadas}
                  </Title>
                </div>
                <CheckCircleIcon style={{ fontSize: '32px', color: '#1890ff' }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #52c41a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Monto Total</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#52c41a' }}>
                    ${estadisticas.totalMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <CheckCircleIcon style={{ fontSize: '32px', color: '#52c41a' }} />
              </div>
            </Card>

            <Card style={{ borderTop: '4px solid #faad14' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Promedio</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: '#faad14' }}>
                    ${estadisticas.promedioMonto.toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                  </Title>
                </div>
                <WarningOutlined style={{ fontSize: '32px', color: '#faad14' }} />
              </div>
            </Card>

            {estadisticas.topProveedores.length > 0 && (
              <Card style={{ borderTop: `4px solid ${theme.colors.secondary}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Top Proveedor</Text>
                    <Title level={5} style={{ margin: '8px 0 0 0' }}>
                      {estadisticas.topProveedores[0]?.nombre || 'N/A'}
                    </Title>
                    <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                      {estadisticas.topProveedores[0]?.count || 0} órdenes
                    </Text>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        <StandardCard 
          title="Órdenes de Compra"
          extra={
            <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <ExportExcelButton
                onClick={handleExportExcel}
                size="small"
                disabled={filteredData.length === 0}
              />
              <ExportPDFButton
                onClick={handleExportPDF}
                size="small"
                disabled={filteredData.length === 0}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOrdenCompraNueva}
                size="small"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500
                }}
              >
                Nueva Orden
              </Button>
            </Space>
          }
        >
          {/* Búsqueda global */}
          <div style={{ marginBottom: '16px' }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
              placeholder="Buscar en todos los campos..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              size="large"
              style={{ 
                width: '100%',
                maxWidth: '600px'
              }}
            />
          </div>

          {/* Mensaje de resultados filtrados */}
          {globalSearch && (
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px 12px', 
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.styles.borderRadius,
              fontSize: '14px',
              color: theme.colors.textSecondary
            }}>
              Mostrando {filteredData.length} de {ordenes.length} órdenes
              {globalSearch && ` que coinciden con "${globalSearch}"`}
            </div>
          )}

          <Table
            loading={loading || isLoading}
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} órdenes`,
            }}
            size="middle"
            onRow={(record) => ({
              onMouseEnter: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa';
              },
              onMouseLeave: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
              },
              style: {
                transition: 'background-color 0.2s ease'
              }
            })}
          />
        </StandardCard>
      </div>
    </App>
  );
};

export default ListarOrdenCompra;